import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { SubscriptionStatus, SubscriptionPlanCode } from "common/models/types";
import {
  AdminSubscriptionRow,
  AdminSubscriptionStats,
} from "common/types/adminSubscription";
import {
  fetchAdminSubscriptions,
  fetchAdminSubscriptionStats,
  cancelAdminSubscription,
  retryAdminSubscription,
} from "api/adminSubscriptionService";
import AdminSubscriptionsTemplate from "components/templates/AdminSubscriptionsTemplate";
import SubscriptionDetail from "components/templates/AdminSubscriptionsTemplate/SubscriptionDetail";
import GrantPremiumSlide from "components/templates/AdminSubscriptionsTemplate/GrantPremiumSlide";
import Button from "components/atoms/Button";
import Spinner from "components/atoms/Spinner";
import { SlideManager } from "components/organisms/CommonUI/SlidePopup/SlideManager";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import { log, LogLevel } from "utils/LogUtil";

const LIMIT = 50;

const PageContainer = styled.div`
  padding: 24px;
  min-height: 100vh;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
`;

const SpinnerContainer = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 1000;
`;

const Subscriptions: React.FC = () => {
  const [rows, setRows] = useState<AdminSubscriptionRow[]>([]);
  const [stats, setStats] = useState<AdminSubscriptionStats | null>(null);
  const [statusFilter, setStatusFilter] = useState<SubscriptionStatus | undefined>(undefined);
  const [planFilter, setPlanFilter] = useState<SubscriptionPlanCode | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  // 동시 실행 방지 — 슬라이드 버튼은 정적 노드라 isBusy 상태로 비활성화되지 않으므로 ref로 가드.
  const busyRef = useRef(false);

  const runExclusive = useCallback(async (fn: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setIsBusy(true);
    try {
      await fn();
    } finally {
      busyRef.current = false;
      setIsBusy(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    const res = await fetchAdminSubscriptionStats();
    if (res.error === 0 && res.data) setStats(res.data);
  }, []);

  const loadRows = useCallback(
    async (pageToLoad = 1, status = statusFilter, plan = planFilter) => {
      const isInitial = pageToLoad === 1;
      if (isInitial) setIsLoading(true);
      else setIsLoadingMore(true);
      try {
        const res = await fetchAdminSubscriptions({
          status,
          planCode: plan,
          limit: LIMIT,
          offset: (pageToLoad - 1) * LIMIT,
        });
        const items = res.error === 0 && res.data ? res.data.items : [];
        setRows((prev) => (isInitial ? items : [...prev, ...items]));
        setHasMore(items.length === LIMIT);
        setPage(pageToLoad);
        if (res.error !== 0) {
          ToastManager.show({ content: res.message || "구독자 목록을 불러오지 못했습니다." });
        }
      } catch (err) {
        log(LogLevel.ERROR, "구독자 목록 로드 오류:", err);
        ToastManager.show({ content: "구독자 목록을 불러오지 못했습니다." });
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [statusFilter, planFilter]
  );

  useEffect(() => {
    loadRows(1, statusFilter, planFilter);
    loadStats();
  }, [statusFilter, planFilter, loadRows, loadStats]);

  // 이름/이메일 클라이언트 로컬 필터 (서버 검색 없음 — 이름/전화 암호화)
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.user?.email || "").toLowerCase().includes(q) ||
        (r.user?.name || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const refreshAll = useCallback(() => {
    loadRows(1, statusFilter, planFilter);
    loadStats();
  }, [loadRows, loadStats, statusFilter, planFilter]);

  const handleLoadMore = useCallback(() => {
    if (!isLoadingMore && hasMore) loadRows(page + 1, statusFilter, planFilter);
  }, [isLoadingMore, hasMore, page, loadRows, statusFilter, planFilter]);

  const handleRetry = useCallback(
    (row: AdminSubscriptionRow) =>
      runExclusive(async () => {
        const res = await retryAdminSubscription(row.id);
        if (res.error === 0) {
          ToastManager.show({ content: `복구 완료: ${res.data?.status ?? ""} (${res.data?.action ?? ""})` });
          SlideManager.hide();
          setSelectedId(null);
          refreshAll();
        } else {
          ToastManager.show({ content: res.message || "복구에 실패했습니다." });
        }
      }),
    [refreshAll, runExclusive]
  );

  const handleCancel = useCallback(
    (row: AdminSubscriptionRow) => {
      ConfirmManager.show({
        title: "구독 취소",
        body: row.isComp
          ? "무료 제공 구독을 즉시 종료(EXPIRED)합니다. 계속하시겠습니까?"
          : "이 구독을 강제 해지합니다. 유료 구독은 잔여 기간까지 유지(CANCELLED)됩니다. 계속하시겠습니까?",
        buttons: [
          <Button
            key="confirm"
            buttonType="button"
            color="Negative"
            size="Medium"
            type="Text"
            variant="Contained"
            onClick={() => {
              ConfirmManager.hide();
              runExclusive(async () => {
                const res = await cancelAdminSubscription(row.id, "관리자 수동 취소");
                if (res.error === 0) {
                  ToastManager.show({ content: `처리 완료: ${res.data?.status ?? ""}` });
                  SlideManager.hide();
                  setSelectedId(null);
                  refreshAll();
                } else {
                  ToastManager.show({ content: res.message || "구독 취소에 실패했습니다." });
                }
              });
            }}
          >
            취소 진행
          </Button>,
        ],
      });
    },
    [refreshAll, runExclusive]
  );

  const openDetail = useCallback(
    (row: AdminSubscriptionRow) => {
      if (selectedId === row.id) {
        SlideManager.hide();
        setSelectedId(null);
        return;
      }
      setSelectedId(row.id);
      SlideManager.show({
        body: (
          <SubscriptionDetail
            row={row}
            isBusy={isBusy}
            onCancel={handleCancel}
            onRetry={handleRetry}
          />
        ),
        width: "560px",
        direction: "right",
        needCloseButton: true,
        padding: "0px",
        onClose: () => setSelectedId(null),
      });
    },
    [selectedId, isBusy, handleCancel, handleRetry]
  );

  const openGrant = useCallback(() => {
    SlideManager.show({
      body: (
        <GrantPremiumSlide
          onSuccess={() => {
            SlideManager.hide();
            refreshAll();
          }}
        />
      ),
      width: "480px",
      direction: "right",
      needCloseButton: true,
      padding: "0px",
    });
  }, [refreshAll]);

  if (isLoading && rows.length === 0) {
    return (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    );
  }

  return (
    <PageContainer>
      <AdminSubscriptionsTemplate
        rows={filteredRows}
        stats={stats}
        statusFilter={statusFilter}
        planFilter={planFilter}
        search={search}
        selectedId={selectedId}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onChangeStatus={setStatusFilter}
        onChangePlan={setPlanFilter}
        onChangeSearch={setSearch}
        onViewRow={openDetail}
        onLoadMore={handleLoadMore}
        onGrant={openGrant}
      />
    </PageContainer>
  );
};

export default Subscriptions;
